// Eingabe-Schemas der API. Server und Apps nutzen dieselben Schemas, damit
// ein Formular nicht etwas durchlässt, das der Server danach ablehnt.
// Feldnamen in der API sind camelCase; die Datenbank speichert snake_case
// (postgres.js übersetzt das, siehe server/src/db.ts).

import { z } from "zod";
import {
  APPLIANCE_ACTIONS,
  DRIVEWAY_TYPES,
  ELEVATOR_SIZES,
  PARKING_OPTIONS,
  STAIRWELL_TYPES,
  STEPS_TO_DOOR,
  KITCHEN_ANSWERS,
  KITCHEN_APPLIANCES,
  FLOOR_MAX,
  FLOOR_MIN,
  HALL_LAYOUTS,
  HALL_SECTION_KINDS,
  HALL_SECTION_MAX,
  HALL_LENGTH_MAX,
  HALL_LENGTH_MIN,
  HALL_SHAPES,
  ROOM_PHOTO_MODES,
  LAMP_KINDS,
  LAMP_MAX,
  ROOM_SHAPES,
  ROOM_WALLS,
  ROOM_DOOR_KINDS,
  ROOM_DOOR_STATUS,
  ROOM_DOOR_MAX,
  HALL_SIDES,
  HALL_TURNS,
  SPECIAL_ITEM_KEYS,
  SPECIAL_KINDS,
  BILLING_INTERVALS,
  EU_COUNTRIES,
  PLAN_IDS,
  REFERRAL_CODE_RE,
  SOURCE_PATTERN,
  FILL_LEVELS,
  CABINET_ANSWERS,
  INVITE_CHANNELS,
  ITEM_ACTIONS,
  OFFER_STATUS,
  ITEM_FLAGS,
  MARK_ACTIONS,
  PHOTO_STEPS,
  ROOM_TYPES,
  SERVICES,
} from "./konstanten.ts";

const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === "" ? null : s))
    .nullable();

export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(200));
export const isoDate = z.iso.date();
export const uuid = z.uuid();

// ---------------------------------------------------------------- Unternehmen

export const registerSchema = z.object({
  companyName: text(120).min(2),
  name: text(120).min(2),
  email: emailSchema,
  password: z.string().min(10).max(200),
  /** AGB für Firmen akzeptiert (Pflicht seit 08.10.2026). */
  agb: z.literal(true),
  /** Empfehlungscode einer anderen Firma (optional; ein ungültiger Code verhindert die Anmeldung nie). */
  referralCode: z.string().trim().toUpperCase().regex(REFERRAL_CODE_RE).optional().catch(undefined),
  /** Herkunft (?von= auf Website, Privat-PDF, Demo) für die Messung; Unbrauchbares wird ignoriert. */
  herkunft: z.string().trim().toLowerCase().regex(SOURCE_PATTERN).optional().catch(undefined),
});

/** Nach dem Umzug: tatsächlich geladenes Volumen in m³ (null = Angabe löschen). */
export const loadedSchema = z.object({ m3: z.number().gt(0).lt(1000).nullable() });

/** Zählimpuls der Website und der Registrierung (Messung ohne Cookies). */
export const metricBeaconSchema = z.object({
  /** Website: Pfad der Seite. */
  s: z.string().regex(/^\/[a-z0-9\-/]{0,80}$/).optional(),
  /** Herkunftsmerkmal ?von=. */
  v: z.string().toLowerCase().regex(SOURCE_PATTERN).optional().catch(undefined),
  /** Art: Seite (Website) oder Registrierung (Business-App). */
  k: z.enum(["seite", "registrieren"]).default("seite"),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});

/** Mitarbeiter einladen / Einladung annehmen (Auftrag 3, Block 3). */
export const teamInviteSchema = z.object({ email: emailSchema }).strict();
export const teamAcceptSchema = z
  .object({ token: z.string().trim().min(20).max(200), name: text(120).min(2), password: z.string().min(10).max(200) })
  .strict();

export const tokenSchema = z.object({ token: z.string().trim().min(20).max(200) });
/** Kunden-Version: Anmeldung ohne Passwort per Code oder Link. */
export const privateSignInSchema = z.object({ email: emailSchema }).strict();
export const privateConfirmSchema = z.union([
  z.object({ email: emailSchema, code: z.string().trim().regex(/^\d{6}$/) }).strict(),
  z.object({ link: z.string().trim().min(20).max(200) }).strict(),
]);
/** Aufnahme-Code einer Privat-Aufnahme, tolerant: "k7qm 2xpa" → "K7QM-2XPA". */
export const surveyCodeSchema = z
  .string()
  .transform((s) => s.toUpperCase().replace(/[^A-Z0-9]/g, ""))
  .pipe(z.string().regex(/^[A-Z0-9]{8}$/))
  .transform((s) => `${s.slice(0, 4)}-${s.slice(4)}`);
export const transferRequestSchema = z.object({ code: surveyCodeSchema }).strict();
export const transferDecisionSchema = z.object({ accept: z.boolean() }).strict();
/** Rechnungsdaten der Firma (Block 7). USt-IdNr. nur nötig für Reverse Charge (EU außerhalb DE). */
export const billingDataSchema = z
  .object({
    name: text(120).min(2),
    street: text(120).min(3),
    zip: text(12).min(3),
    city: text(80).min(2),
    country: z.enum(EU_COUNTRIES),
    vatId: z
      .string()
      .transform((s) => s.toUpperCase().replace(/[^A-Z0-9]/g, ""))
      .pipe(z.string().regex(/^([A-Z]{2}[A-Z0-9]{2,13})?$/))
      .optional(),
  })
  .strict();
/** Buchung eines Abos: AGB-Zustimmung ist Pflicht. */
export const checkoutPlanSchema = z.object({ plan: z.enum(PLAN_IDS), interval: z.enum(BILLING_INTERVALS), agb: z.literal(true) }).strict();
/** Privat-Aufnahme bezahlen: Verbraucher-AGB und Verzicht auf das Widerrufsrecht (sofortige Ausführung). */
export const privatePaySchema = z.object({ agb: z.literal(true), verzicht: z.literal(true) }).strict();
/** Bezahlung über Google Play Billing (Kunden-App): Einwilligungen wie bei Stripe, dazu der Kauf-Token. */
export const playPaySchema = z
  .object({ agb: z.literal(true), verzicht: z.literal(true), purchaseToken: z.string().trim().min(10).max(4000), productId: z.string().trim().min(1).max(100) })
  .strict();
export const passwordForgotSchema = z.object({ email: emailSchema });
export const passwordResetSchema = z.object({ token: z.string().trim().min(20).max(200), password: z.string().min(10).max(200) });
export const passwordChangeSchema = z.object({ current: z.string().min(1).max(200), password: z.string().min(10).max(200) });
export const emailChangeSchema = z.object({ email: emailSchema, password: z.string().min(1).max(200) });
/** Konto: Name und/oder Push an/aus (13.10.2026, Push in der Business-App je Nutzer abschaltbar). */
export const accountPatchSchema = z
  .object({ name: text(120).min(2).optional(), pushEnabled: z.boolean().optional() })
  .strict()
  .refine((p) => p.name !== undefined || p.pushEnabled !== undefined, { message: "leer" });

export const companyPatchSchema = z
  .object({
    name: text(120).min(2),
    street: optionalText(120),
    zip: optionalText(10),
    city: optionalText(80),
    phone: optionalText(40),
    email: emailSchema,
    website: optionalText(200),
    /** Empfänger der Benachrichtigungen; null = Firmen-E-Mail. */
    notifyEmail: emailSchema.nullable(),
  })
  .partial()
  .strict();

export const inviteCreateSchema = z.object({
  channel: z.enum(INVITE_CHANNELS),
  customerName: optionalText(120).optional(),
  /** Optional: Damit erinnert Umzugsscan den Kunden automatisch (2 und 5 Tage nach der Einladung). */
  customerEmail: emailSchema.nullable().optional(),
});

/** Kontaktformular im Impressum. `website` ist ein verstecktes Lockfeld gegen Spam-Programme. */
export const contactSchema = z.object({
  name: text(120).min(2),
  email: emailSchema,
  message: text(5000).min(10),
  website: z.string().max(500).optional(),
});

export const pushTokenSchema = z.object({
  token: text(300).min(10),
  platform: z.enum(["android", "ios", "web"]),
});

// ------------------------------------------------------------ Kundenerfassung

export const startSchema = z.union([
  z.object({ slug: text(80).min(1) }).strict(),
  z.object({ invite: text(80).min(1) }).strict(),
]);

export const servicesSchema = z.partialRecord(z.enum(SERVICES), z.boolean());

export const surveyPatchSchema = z
  .object({
    contactName: optionalText(120),
    phone: optionalText(40),
    email: emailSchema.nullable(),
    dateFixed: z.boolean().nullable(),
    dateFrom: isoDate.nullable(),
    dateTo: isoDate.nullable(),
    services: servicesSchema,
    customerNote: optionalText(2000),
    /** Haushalt, optional (Migration 0021): Grundlage der Gegenprobe im Bericht. */
    livingAreaM2: z.number().int().min(10).max(2000).nullable(),
    persons: z.number().int().min(1).max(30).nullable(),
    /** Pflicht-Einwilligung Datenschutz; nur `true` ist sinnvoll. */
    consentPrivacy: z.literal(true),
    /** Freiwillig: Fotos dürfen zur Verbesserung der Erkennung gespeichert werden (CONSENT_TRAINING_LABEL). */
    consentTraining: z.boolean(),
    /** Hinweis vor dem ersten Foto bestätigt (photoNoticeText); nur `true`. */
    photoNotice: z.literal(true),
    /** Sonderteile und Zerbrechliches (eigene Zwischenschritte), ganze Liste. */
    specialItems: z
      .array(
        z
          .object({
            kind: z.enum(SPECIAL_KINDS),
            key: z.string().refine((k) => SPECIAL_ITEM_KEYS.includes(k)),
            qty: z.number().int().min(1).max(99),
            note: optionalText(200),
          })
          .strict(),
      )
      .max(30),
    /** Angaben zur Küche (eigener Schritt), ganzes Objekt. */
    kitchen: z
      .object({
        moves: z.enum(KITCHEN_ANSWERS),
        contents: z.enum(KITCHEN_ANSWERS),
        appliances: z.partialRecord(z.enum(KITCHEN_APPLIANCES), z.enum(APPLIANCE_ACTIONS)),
      })
      .partial()
      .strict(),
    /** Lampen ab- und anbauen (Migration 0034), ganzes Objekt. */
    lamps: z
      .object({
        wanted: z.boolean().nullable(),
        items: z
          .array(z.object({ id: z.uuid(), roomId: z.uuid(), kind: z.enum(LAMP_KINDS), abbau: z.boolean(), anbau: z.boolean() }).strict())
          .max(LAMP_MAX),
        neu: z.partialRecord(z.enum(LAMP_KINDS), z.number().int().min(0).max(30)),
      })
      .strict(),
    /** Freier Fortschritt der Erfassungs-App (aktueller Schritt usw.). */
    progress: z.record(z.string(), z.unknown()),
  })
  .partial()
  .strict()
  .refine((p) => !p.progress || JSON.stringify(p.progress).length <= 20_000, {
    message: "progress zu groß",
    path: ["progress"],
  });

/** Angebotsstand, interne Notiz und Wiedervorlage (PATCH /aufnahmen/:id/angebot). */
export const offerPatchSchema = z
  .object({
    offerStatus: z.enum(OFFER_STATUS),
    internalNote: optionalText(2000),
    followUpOn: isoDate.nullable(),
  })
  .partial()
  .strict();

/** Fotos nachfordern (POST /aufnahmen/:id/nachfordern): Nachricht der Firma an den Kunden. */
export const photoRequestSchema = z.object({ message: text(1000).min(5) }).strict();

export const addressSchema = z
  .object({
    street: optionalText(120),
    zip: z
      .string()
      .trim()
      .regex(/^\d{5}$/)
      .nullable(),
    city: optionalText(80),
    floor: z.number().int().min(-3).max(40).nullable(),
    elevator: z.boolean().nullable(),
    walkDistanceM: z.number().int().min(0).max(1000).nullable(),
    // Zugang und Parken, optional (Migration 0020)
    stairwell: z.enum(STAIRWELL_TYPES).nullable(),
    elevatorSize: z.enum(ELEVATOR_SIZES).nullable(),
    stepsToDoor: z.enum(STEPS_TO_DOOR).nullable(),
    parking: z.enum(PARKING_OPTIONS).nullable(),
    driveway: z.enum(DRIVEWAY_TYPES).nullable(),
    note: optionalText(500),
    // Neue Adresse steht noch nicht fest (Migration 0037): dann genügen PLZ und Ort.
    pending: z.boolean(),
  })
  .partial()
  .strict();

/** Ein Abschnitt des Flurs (14.10.2026). */
export const hallSectionSchema = z
  .object({
    nr: z.number().int().min(1).max(HALL_SECTION_MAX),
    art: z.enum(HALL_SECTION_KINDS),
    richtung: z.enum(HALL_TURNS).nullable(),
    von: z.number().int().min(1).max(HALL_SECTION_MAX).nullable(),
    bei: z.number().min(0).max(1).nullable().optional(),
    kurz: z.boolean().optional(),
    eingang: z.object({ seite: z.enum(["anfang", "links", "rechts"]), pos: z.number().min(0).max(1) }).strict().nullable().optional(),
    // Länge des Gangs (31.10.2026, Ecke verschiebbar), Anteil der Standardlänge.
    laenge: z.number().min(HALL_LENGTH_MIN).max(HALL_LENGTH_MAX).nullable().optional(),
  })
  .strict();

/** Tür eines Raums (Migration 0032), siehe RoomDoor. */
export const roomDoorSchema = z
  .object({
    id: z.uuid(),
    photoId: z.uuid().nullable(),
    x: z.number().min(0).max(1).nullable(),
    y: z.number().min(0).max(1).nullable(),
    wall: z.enum(ROOM_WALLS).nullable(),
    kind: z.enum(ROOM_DOOR_KINDS),
    status: z.enum(ROOM_DOOR_STATUS),
    to: z.uuid().nullable(),
    source: z.enum(["ki", "kunde"]),
  })
  .strict();

export const roomSchema = z
  .object({
    type: z.enum(ROOM_TYPES),
    name: text(80).min(1),
    sort: z.number().int().min(0).max(1000),
    confirmedComplete: z.boolean(),
    cabinets: z.enum(CABINET_ANSWERS).nullable(),
    // Grundriss (Migration 0017)
    floor: z.number().int().min(FLOOR_MIN).max(FLOOR_MAX),
    hallRoomId: z.uuid().nullable(),
    hallSide: z.enum(HALL_SIDES).nullable(),
    hallOrder: z.number().int().min(0).max(99).nullable(),
    doorPhotoId: z.uuid().nullable(),
    doorX: z.number().min(0).max(1).nullable(),
    doorY: z.number().min(0).max(1).nullable(),
    hallLayout: z.enum(HALL_LAYOUTS).nullable(),
    // Flur als Weg aus Abschnitten (Migration 0030): Form und Abschnitte am Flur, Abschnitt der Tür am Raum.
    hallShape: z.enum(HALL_SHAPES).nullable(),
    hallSections: z.array(hallSectionSchema).max(HALL_SECTION_MAX).nullable(),
    hallSection: z.number().int().min(1).max(HALL_SECTION_MAX).nullable(),
    // Lage der Tür an ihrer Flurwand (Migration 0035), vom Kunden in der Skizze gezogen.
    hallPos: z.number().min(0).max(1).nullable(),
    // Raum nach Wänden (Migration 0031): Aufnahmeart und Form.
    photoMode: z.enum(ROOM_PHOTO_MODES).nullable(),
    roomShape: z.enum(ROOM_SHAPES).nullable(),
    // Türen je Raum (Migration 0032): Wand des Elternraums, an der die Tür zu diesem Raum liegt; Türen dieses Raums.
    doorWall: z.enum(ROOM_WALLS).nullable(),
    doors: z.array(roomDoorSchema).max(ROOM_DOOR_MAX).nullable(),
  })
  .partial({
    sort: true,
    confirmedComplete: true,
    cabinets: true,
    floor: true,
    hallRoomId: true,
    hallSide: true,
    hallOrder: true,
    doorPhotoId: true,
    doorX: true,
    doorY: true,
    hallLayout: true,
    hallShape: true,
    hallSections: true,
    hallSection: true,
    hallPos: true,
    photoMode: true,
    roomShape: true,
    doorWall: true,
    doors: true,
  })
  .strict();

export const roomPatchSchema = roomSchema.partial();

/**
 * Metadaten zum Foto-Upload (Query-Parameter, daher z.coerce). Breite und
 * Höhe liest der Server selbst aus dem JPEG.
 */
export const photoUploadQuerySchema = z.object({
  roomId: uuid,
  step: z.enum(PHOTO_STEPS),
  /** Abschnitt des Flurs (14.10.2026), nur bei Flurfotos. */
  section: z.coerce.number().int().min(1).max(HALL_SECTION_MAX).optional(),
  /** Schärfe aus dem Browser (Varianz des Laplace-Filters); höher = schärfer. */
  blurScore: z.coerce.number().min(0).max(100_000).optional(),
  /** Mittlere Helligkeit 0..255. */
  brightness: z.coerce.number().min(0).max(255).optional(),
});

export const marksSchema = z
  .array(
    z.object({
      x: z.number().min(0).max(1),
      y: z.number().min(0).max(1),
      action: z.enum(MARK_ACTIONS),
      label: optionalText(80).optional(),
    }),
  )
  .max(50);

// ----------------------------------------------------- Bericht korrigieren

const itemFields = {
  roomId: uuid.nullable(),
  name: text(120).min(1),
  catalogKey: text(60).nullable(),
  qty: z.number().int().min(1).max(999),
  m3Each: z.number().min(0).max(20),
  kgEach: z.number().min(0).max(2000).nullable(),
  action: z.enum(ITEM_ACTIONS),
  flags: z.array(z.enum(ITEM_FLAGS)).max(6),
  position: z.enum(["links", "gegenueber", "rechts", "tuerseite", "mitte"]).nullable(),
};

export const inventoryItemCreateSchema = z
  .object(itemFields)
  .partial({ catalogKey: true, flags: true, position: true, action: true, kgEach: true })
  .strict();

export const inventoryItemPatchSchema = z.object(itemFields).partial().strict();

export const assessmentPatchSchema = z
  .object({
    fillLevel: z.enum(FILL_LEVELS),
    bookBoxes: z.number().int().min(0).max(500),
    largeCabinets: z.boolean(),
    cabinetsOpen: z.boolean(),
  })
  .partial()
  .strict();
